/**
 * Cart settle: oculta el carrito (drawer y página) tras un overlay con spinner
 * mientras las operaciones sobre el carrito siguen en marcha (tema, GiftTiers,
 * Wholesale, HulkDiscounts...), y lo muestra una sola vez cuando se estabiliza.
 *
 * No depende de los eventos de cada app: envuelve fetch y XMLHttpRequest y
 * cuenta las peticiones de modificación del carrito. Cuando no quedan peticiones
 * en vuelo y pasa un margen de quietud (QUIET_MS), lanza un único 'cart:refresh'
 * para pintar el estado final y quita el overlay. El estado se refleja en la
 * clase `cart-settling` de <html>.
 *
 * Debe cargarse ANTES de content_for_header para envolver fetch/XHR primero.
 */
(function () {
  if (window.__cartSettle) return;

  var QUIET_MS = 450;      // sin actividad antes de considerar el carrito estable
  var RENDER_MS = 200;     // margen tras el refresco final para que se pinte
  var REFRESH_WAIT_MS = 800; // si nadie reacciona al refresco final, cerramos
  var MAX_MS = 6000;       // tope de seguridad por episodio
  var CLASS = 'cart-settling';
  var LEAVE_CLASS = 'cart-settle-leaving';
  var FADE_MS = 250;       // debe coincidir con la transición de cart-settle.css
  var EVAL_MS = 300;       // espera tras una lectura de /cart.js por si le sigue una mutación

  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|#|$)/;
  var CARTJS_RE = /\/cart\.(js|json)(\?|#|$)/;
  var SECTIONS_RE =/[?&]sections=[^&]*(cart-drawer|main-cart|cart-bubble)/;
  var MUTATION_KEYS = ['id', 'items', 'line', 'quantity', 'updates'];

  var pending = 0;
  var phase = 'idle'; // idle | busy | finalizing
  var HIDE_GRACE_MS = 150; // retraso al quitar el overlay para absorber operaciones encadenadas
  var quietTimer, finalTimer, maxTimer, hideTimer, fadeTimer;

  function urlOf(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    return input.url || String(input);
  }

  // Ignora actualizaciones que solo guardan nota/atributos del carrito.
  function isMutationBody(body) {
    if (typeof body !== 'string') return true;
    try {
      var data = JSON.parse(body);
      if (!data || typeof data !== 'object') return true;
      return MUTATION_KEYS.some(function (k) { return k in data; });
    } catch (e) {
      return /(^|&)(id|updates|line|quantity|items)(\[|=)/.test(body);
    }
  }

  function isMutation(url, body) {
    return MUTATION_RE.test(url) && isMutationBody(body);
  }

  // ¿La mutación pide al servidor las secciones del drawer en su propia respuesta?
  // (formulario de producto y cambios de cantidad del drawer lo hacen: el tema pinta
  // el drawer con esa respuesta, sin necesidad de volver a pedir ?sections=).
  function requestsDrawerRender(body) {
    try {
      var s = '';
      if (typeof FormData !== 'undefined' && body instanceof FormData) s = String(body.get('sections') || '');
      else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) s = String(body.get('sections') || '');
      else if (typeof body === 'string') s = body;
      return /cart-drawer/.test(s);
    } catch (e) {
      return false;
    }
  }

  // Verdadero si la última mutación en completarse fue una cuyo propio resultado ya
  // pintó el tema en el drawer. Se envía en el cart:refresh final (detail.rendered).
  var lastDoneRendered = false;

  // Al apagar no se retira el overlay de golpe: pasa por LEAVE_CLASS, que lo
  // mantiene dibujado mientras hace fade-out (ver cart-settle.css).
  function setBusy(on) {
    var cl = document.documentElement.classList;
    clearTimeout(fadeTimer);
    if (on) {
      cl.remove(LEAVE_CLASS);
      cl.add(CLASS);
    } else if (cl.contains(CLASS)) {
      cl.add(LEAVE_CLASS);
      cl.remove(CLASS);
      fadeTimer = setTimeout(function () { cl.remove(LEAVE_CLASS); }, FADE_MS + 50);
    }
  }

  function finish() {
    clearTimeout(quietTimer);
    clearTimeout(finalTimer);
    clearTimeout(maxTimer);
    phase = 'idle';
    // Pequeña gracia: si arranca otra operación enseguida (p. ej. un regalo
    // añadido tras el refresco final) el overlay no parpadea ni reinicia el spinner.
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      if (phase === 'idle') setBusy(false);
    }, HIDE_GRACE_MS);
  }

  function startMutation() {
    pending++;
    clearTimeout(quietTimer);
    clearTimeout(finalTimer);
    clearTimeout(hideTimer);
    if (phase !== 'busy') {
      phase = 'busy';
      lastDoneRendered = false; // episodio nuevo: se decide con la última mutación en completarse
      setBusy(true);
      clearTimeout(maxTimer);
      maxTimer = setTimeout(finish, MAX_MS);
    }
  }

  // kind: 'mutation' | 'eval' (lectura de /cart.js) | 'render' (lectura de sections)
  function endRequest(kind) {
    pending = Math.max(0, pending - 1);
    if (pending > 0) return;

    var wait = kind === 'eval' ? EVAL_MS : null;
    if (phase === 'busy') {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(finalize, wait || QUIET_MS);
    } else if (phase === 'finalizing') {
      clearTimeout(finalTimer);
      finalTimer = setTimeout(finish, wait || RENDER_MS);
    }
  }

  // Un único repintado con el estado final; sus lecturas de sections cuentan
  // como en vuelo para no quitar el overlay antes de que se pinten.
  function finalize() {
    if (pending > 0) return;
    phase = 'finalizing';
    finalTimer = setTimeout(finish, REFRESH_WAIT_MS);
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: { source: 'cart-settle', rendered: lastDoneRendered }
    }));
  }

  function track(url, body, promiseLike) {
    var kind = null;
    if (isMutation(url, body)) {
      kind = 'mutation';
    } else if (phase !== 'idle' && CARTJS_RE.test(url)) {
      // Las apps leen /cart.js para decidir si aplican otro regalo/descuento:
      // es actividad inminente, aunque aún no sea una mutación.
      kind = 'eval';
    } else if (phase === 'finalizing' && SECTIONS_RE.test(url)) {
      kind = 'render';
    }
    if (!kind) return;

    if (kind === 'mutation') {
      startMutation();
    } else {
      pending++;
      clearTimeout(quietTimer);
      clearTimeout(finalTimer);
    }
    promiseLike(function () { endRequest(kind); });
  }

  // Lecturas de /cart.js compartidas. Tema, GiftTiers (x2), qty-discount, WSAIO...
  // leen /cart.js por su cuenta tras cada cambio (6+ lecturas simultáneas, luego otra
  // ráfaga tras el cart:refresh), y cada lectura reinicia la espera de quietud de este
  // mismo overlay. Aquí, la capa más baja, las lecturas GET que llegan mientras otra
  // está en vuelo (o acaba de terminar) reutilizan su respuesta. Cualquier mutación
  // del carrito (al empezar y al terminar) invalida lo compartido, así que una lectura
  // posterior a un cambio siempre va a red.
  var CARTJS_SHARE_MS = 1000;
  var cartEpoch = 0;
  var cartRead = null; // { epoch, at, promise }

  function isGetRequest(input, init) {
    var method = (init && init.method) || (input && input.method) || 'GET';
    return String(method).toUpperCase() === 'GET';
  }

  function bumpCartEpoch() {
    cartEpoch++;
    cartRead = null;
  }

  // fetch
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = urlOf(input);
      var isMut = MUTATION_RE.test(url);

      if (!isMut && CARTJS_RE.test(url) && isGetRequest(input, init)) {
        var now = Date.now();
        if (cartRead && cartRead.epoch === cartEpoch && now - cartRead.at < CARTJS_SHARE_MS) {
          return cartRead.promise.then(function (r) { return r.clone(); });
        }
        var base = nativeFetch.apply(this, arguments);
        var entry = { epoch: cartEpoch, at: now, promise: base };
        cartRead = entry;
        var drop = function () { if (cartRead === entry) cartRead = null; };
        base.then(function (r) { if (!r.ok) drop(); }, drop);
        // Todos los consumidores (también el primero) reciben un clon: la respuesta
        // base queda sin consumir para poder clonarla después.
        var shared = base.then(function (r) { return r.clone(); });
        try {
          track(url, undefined, function (done) { shared.then(done, done); });
        } catch (e) { /* nunca romper la petición original */ }
        return shared;
      }

      if (isMut) bumpCartEpoch();
      var promise = nativeFetch.apply(this, arguments);
      if (isMut) promise.then(bumpCartEpoch, bumpCartEpoch);
      try {
        var body = init && init.body !== undefined ? init.body : (input && input.body);
        track(url, body, function (done) {
          promise.then(done, done);
        });
        if (isMutation(url, body)) {
          var wantsRender = requestsDrawerRender(body);
          promise.then(
            function (r) { lastDoneRendered = wantsRender && !!(r && r.ok); },
            function () { lastDoneRendered = false; }
          );
        }
      } catch (e) { /* nunca romper la petición original */ }
      return promise;
    };
  }

  // XMLHttpRequest (jQuery.ajax y apps antiguas)
  var xhrProto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (xhrProto) {
    var nativeOpen = xhrProto.open;
    var nativeSend = xhrProto.send;
    xhrProto.open = function (method, url) {
      this.__cartSettleUrl = String(url || '');
      return nativeOpen.apply(this, arguments);
    };
    xhrProto.send = function (body) {
      var xhr = this;
      if (MUTATION_RE.test(xhr.__cartSettleUrl || '')) {
        bumpCartEpoch();
        xhr.addEventListener('loadend', bumpCartEpoch);
        // Por XHR (jQuery/apps antiguas) no se sabe si alguien pintó la respuesta.
        xhr.addEventListener('loadend', function () { lastDoneRendered = false; });
      }
      try {
        track(xhr.__cartSettleUrl || '', body, function (done) {
          xhr.addEventListener('loadend', done);
        });
      } catch (e) { /* idem */ }
      return nativeSend.apply(this, arguments);
    };
  }

  window.__cartSettle = {
    isBusy: function () { return phase !== 'idle'; },
    // Contador que sube al empezar y al terminar cualquier mutación del carrito. Quien
    // calcule algo a partir del carrito puede saber si ha cambiado desde la última vez.
    epoch: function () { return cartEpoch; }
  };
})();
